import { useState, useEffect, useCallback } from 'react';
import { Search, RotateCcw, Save, Loader2, Info } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

// Provider rewards are read-only; the markup (margin) that the admin adds on top
// of the face value is stored separately and joined by reward id on the backend.
// Flat markup is entered in ₹ (rupees); percent is a plain number (e.g. 5 = 5%).
const DEFAULT_MARKUP = { markup_type: 'flat', markup_value: '', is_active: false };

// Every face-value denomination (paise) a card offers.
//  - fixed cards: full eligible_fixed_denomination list (sorted asc)
//  - range cards: [min, max] band from eligible_range_denomination
const getDenoms = (card) => {
  const fixed = card.eligible_fixed_denomination || [];
  const range = card.eligible_range_denomination || [];
  if (fixed.length) {
    return { type: 'fixed', values: [...fixed].sort((a, b) => a - b) };
  }
  if (range.length) {
    return { type: 'range', min: range[0], max: range[range.length - 1] };
  }
  if (card.amount) return { type: 'fixed', values: [card.amount] };
  return { type: 'fixed', values: [] };
};

const cardName = (card) =>
  card.brand?.name || card.display_parameters?.name || card.name || 'Gift Card';

// Resulting customer-facing price after applying the markup to a face value (₹).
const computeDisplay = (faceRupees, edit) => {
  const value = Number(edit.markup_value);
  if (!edit.is_active || !value || Number.isNaN(value)) return faceRupees;
  if (edit.markup_type === 'percent') return Math.round(faceRupees * (1 + value / 100));
  return faceRupees + value; // flat
};

export default function AdminGiftCards() {
  const [cards, setCards] = useState([]);
  const [edits, setEdits] = useState({}); // reward_id -> { markup_type, markup_value, is_active }
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [search, setSearch] = useState('');
  const [adminApiMissing, setAdminApiMissing] = useState(false);
  const [dirty, setDirty] = useState(() => new Set()); // ids with unsaved edits

  const seedEdits = useCallback((list) => {
    const seeded = {};
    list.forEach((c) => {
      seeded[c.id] = c.markup
        ? {
            markup_type: c.markup.markup_type || 'flat',
            markup_value: c.markup.markup_value ?? '',
            is_active: !!c.markup.is_active
          }
        : { ...DEFAULT_MARKUP };
    });
    setEdits(seeded);
    setDirty(new Set()); // fresh from server → nothing unsaved
  }, []);

  const markClean = (id) =>
    setDirty((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  const fetchCards = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      // Preferred: admin endpoint that merges provider rewards with saved markup.
      const res = await fetch(`${API}/admin/giftcards`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const list = data.items || data || [];
        setCards(list);
        seedEdits(list);
        setAdminApiMissing(false);
        return;
      }
      // Fall back to the public rewards list so the page is usable before the
      // admin markup endpoints exist. Save/reset will surface a clear message.
      setAdminApiMissing(true);
      const pub = await fetch(`${API}/giftcards/razorpay/rewards`, { headers, credentials: 'include' });
      if (pub.ok) {
        const data = await pub.json();
        const list = (data.items || []).filter((item) => item.type === 'gift_card');
        setCards(list);
        seedEdits(list);
      }
    } catch (error) {
      console.error('Failed to fetch gift cards:', error);
      toast.error('Failed to load gift cards');
    } finally {
      setLoading(false);
    }
  }, [seedEdits]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  const setEdit = (id, patch) => {
    setEdits((prev) => ({ ...prev, [id]: { ...DEFAULT_MARKUP, ...prev[id], ...patch } }));
    setDirty((prev) => new Set(prev).add(id)); // any edit marks the row unsaved
  };

  const handleSave = async (card) => {
    const edit = edits[card.id];
    const value = Number(edit.markup_value);
    if (edit.is_active && (!value || value < 0 || Number.isNaN(value))) {
      toast.error('Enter a valid markup value greater than 0');
      return;
    }
    setSavingId(card.id);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/admin/giftcards/${card.id}/markup`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({
          markup_type: edit.markup_type,
          markup_value: value || 0,
          is_active: edit.is_active
        })
      });
      if (!res.ok) {
        if (res.status === 404) {
          toast.error('Markup API not deployed yet. Ask the backend team to add PUT /api/admin/giftcards/:id/markup');
          return;
        }
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save markup');
      }
      toast.success(`Markup saved for ${cardName(card)}`);
      markClean(card.id);
      fetchCards();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingId(null);
    }
  };

  const handleReset = async (card) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Remove markup for ${cardName(card)} and sell at face value?`)) return;
    setSavingId(card.id);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/admin/giftcards/${card.id}/markup`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (!res.ok && res.status !== 404) {
        throw new Error('Failed to reset markup');
      }
      setEdit(card.id, { ...DEFAULT_MARKUP });
      toast.success('Markup removed');
      fetchCards();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingId(null);
    }
  };

  const formatPrice = (value) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value || 0);

  const filtered = cards.filter((c) =>
    cardName(c).toLowerCase().includes(search.trim().toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="admin-giftcards">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl text-secondary">Gift Cards</h1>
          <p className="text-sm text-muted-foreground font-body mt-1">
            Add your margin on top of each card's face value. Customers pay the marked-up price.
            <span className="text-amber-600"> Changes apply only after you press Save on that row.</span>
          </p>
        </div>
        <div className="relative w-full md:w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search gift cards..."
            className="pl-9 rounded-full"
          />
        </div>
      </div>

      {adminApiMissing && (
        <div className="flex items-start gap-3 bg-warning/10 border border-warning/20 text-secondary rounded-2xl p-4">
          <Info className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
          <p className="text-sm font-body">
            Admin markup API not detected yet — showing live gift cards for preview. Saving needs the backend
            endpoints (<span className="font-mono text-xs">GET/PUT/DELETE /api/admin/giftcards</span>).
          </p>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px]">
            <thead>
              <tr className="border-b border-gray-100 bg-surface/50">
                <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">Gift Card</th>
                <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">Face Value</th>
                <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">Markup Type</th>
                <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">Value</th>
                <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">Active</th>
                <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">Selling Price</th>
                <th className="text-right py-4 px-4 font-body font-medium text-muted-foreground text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((card) => {
                const edit = edits[card.id] || DEFAULT_MARKUP;
                const denom = getDenoms(card);
                const rowDirty = dirty.has(card.id);
                const markupActive = edit.is_active && Number(edit.markup_value) > 0;
                const markupLabel = edit.markup_type === 'percent'
                  ? `+${Number(edit.markup_value) || 0}% each`
                  : `+${formatPrice(Number(edit.markup_value) || 0)} each`;
                return (
                  <tr key={card.id} className="border-b border-gray-50 hover:bg-surface/30">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        {(card.display_parameters?.image_url || card.brand?.logo_url) && (
                          <img
                            src={card.display_parameters?.image_url || card.brand?.logo_url}
                            alt=""
                            className="w-10 h-10 rounded-lg object-contain bg-surface flex-shrink-0"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        )}
                        <span className="font-body font-medium text-secondary line-clamp-2">{cardName(card)}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      {denom.type === 'range' ? (
                        <div className="flex items-center gap-2">
                          <span className="font-body text-secondary whitespace-nowrap">
                            {formatPrice(denom.min / 100)} – {formatPrice(denom.max / 100)}
                          </span>
                          <Badge className="bg-primary/10 text-primary">range</Badge>
                        </div>
                      ) : denom.values.length ? (
                        <div className="flex flex-wrap gap-1 max-w-[220px]">
                          {denom.values.map((v, i) => (
                            <span
                              key={i}
                              className="px-2 py-0.5 rounded-md bg-surface border border-gray-100 text-xs font-body text-secondary whitespace-nowrap"
                            >
                              {formatPrice(v / 100)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <select
                        value={edit.markup_type}
                        onChange={(e) => setEdit(card.id, { markup_type: e.target.value })}
                        className="rounded-xl border border-gray-200 px-3 py-2 text-sm font-body bg-white focus:border-primary outline-none"
                      >
                        <option value="flat">Flat (₹)</option>
                        <option value="percent">Percent (%)</option>
                      </select>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-1">
                        <span className="text-muted-foreground text-sm">{edit.markup_type === 'percent' ? '%' : '₹'}</span>
                        <Input
                          type="number"
                          min="0"
                          value={edit.markup_value}
                          onChange={(e) => setEdit(card.id, { markup_value: e.target.value })}
                          placeholder="0"
                          className="w-24 rounded-xl"
                        />
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={edit.is_active}
                        onClick={() => setEdit(card.id, { is_active: !edit.is_active })}
                        className={`relative inline-flex h-5 w-10 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40 ${edit.is_active ? 'bg-primary' : 'bg-gray-300'}`}
                        data-testid={`markup-toggle-${card.id}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${edit.is_active ? 'translate-x-5' : 'translate-x-0.5'}`}
                        />
                      </button>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex flex-col gap-1">
                        {denom.type === 'range' ? (
                          <span className="font-body font-semibold text-secondary whitespace-nowrap">
                            {formatPrice(computeDisplay(denom.min / 100, edit))} – {formatPrice(computeDisplay(denom.max / 100, edit))}
                          </span>
                        ) : denom.values.length ? (
                          <div className="flex flex-wrap gap-1 max-w-[220px]">
                            {denom.values.map((v, i) => {
                              const face = v / 100;
                              const sell = computeDisplay(face, edit);
                              return (
                                <span
                                  key={i}
                                  className={`px-2 py-0.5 rounded-md text-xs font-body whitespace-nowrap border ${sell > face
                                    ? 'bg-success/10 border-success/20 text-success font-semibold'
                                    : 'bg-surface border-gray-100 text-secondary'
                                    }`}
                                >
                                  {formatPrice(sell)}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                        {markupActive && (
                          <Badge className="bg-success/10 text-success w-fit">{markupLabel}</Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end gap-2">
                        {rowDirty && (
                          <span className="text-[11px] font-body font-medium text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                            Unsaved
                          </span>
                        )}
                        <Button
                          size="sm"
                          onClick={() => handleSave(card)}
                          disabled={savingId === card.id}
                          className={`rounded-full ${rowDirty ? 'bg-primary ring-2 ring-primary/30' : 'bg-primary/60'}`}
                        >
                          {savingId === card.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          <span className="ml-1 hidden sm:inline">Save</span>
                        </Button>
                        <button
                          onClick={() => handleReset(card)}
                          disabled={savingId === card.id}
                          title="Reset to face value"
                          className="p-2 hover:bg-surface rounded-lg transition-colors text-muted-foreground"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-primary opacity-50" />
              <p className="text-slate-400 text-sm font-body">Loading gift cards...</p>
            </div>
          ) : (
            filtered.length === 0 && (
              <div className="text-center py-12">
                <p className="text-muted-foreground font-body">No gift cards found</p>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
